'use client'

import { Coins, Clock, ListChecks, RotateCcw, Star, Trophy } from 'lucide-react'

import { formatAttempts, formatTimeLimit } from '../lib/quiz-ui'
import type { Quiz } from '../types/quiz'

import { Box } from '@/shared/ui/Box'
import { Button } from '@/shared/ui/Button'

interface QuizIntroProps {
  quiz: Quiz
  isStarting: boolean
  errorMessage?: string | null
  onStart: () => void
}

const StatTile = ({
  icon,
  label,
  value
}: {
  icon: React.ReactNode
  label: string
  value: string
}) => (
  <Box variant="purple" className="flex items-center gap-3 p-4">
    <span
      className="flex size-9 shrink-0 items-center justify-center rounded-full bg-white/70 text-jacaranda"
      aria-hidden
    >
      {icon}
    </span>
    <span className="min-w-0">
      <span className="block text-xs font-semibold uppercase tracking-wide text-[#7F7FAD]">
        {label}
      </span>
      <span className="block truncate font-hl text-base font-bold text-american-blue">
        {value}
      </span>
    </span>
  </Box>
)

export function QuizIntro({
  quiz,
  isStarting,
  errorMessage,
  onStart
}: QuizIntroProps) {
  const score = quiz.current_score
  const exhausted = quiz.attempts_left === 0
  // Só faz sentido avisar sobre substituição quando refazer é possível.
  const canRepeat = !exhausted

  return (
    <div className="flex flex-col gap-5">
      {/* Herói: o que o aluno vai enfrentar, em números grandes. */}
      <section className="relative overflow-hidden rounded-2xl bg-[linear-gradient(135deg,#4B2A8F_0%,#37206B_55%,#2B1A55_100%)] px-5 py-6">
        <div
          className="absolute -right-8 -top-8 size-32 rounded-full bg-[#8F85F4]/25 blur-3xl"
          aria-hidden
        />
        <div className="relative flex items-end gap-4">
          <div className="min-w-0 grow">
            <p className="text-sm font-bold tracking-[-0.02em] text-[#B9AEE8]">
              Nesta tentativa
            </p>
            <div className="mt-1 flex flex-wrap items-end gap-x-2 leading-none">
              <span className="font-hl text-5xl font-black tracking-[-0.05em] text-white">
                {quiz.questions_per_attempt}
              </span>
              <span className="pb-1 text-lg font-black tracking-[-0.03em] text-[#B9AEE8]">
                {quiz.questions_per_attempt === 1 ? 'pergunta' : 'perguntas'}
              </span>
            </div>
            {quiz.questions_count ? (
              <p className="mt-2 text-sm font-semibold text-[#B9AEE8]">
                sorteadas de um banco de {quiz.questions_count}
              </p>
            ) : null}
          </div>
          <Trophy
            className="size-14 shrink-0 text-[#FFF759] drop-shadow-[0_4px_10px_rgba(255,247,89,0.35)]"
            aria-hidden
          />
        </div>
      </section>

      {quiz.intro ? (
        <p className="text-sm font-medium text-[#7F7FAD]">{quiz.intro}</p>
      ) : null}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <StatTile
          icon={<Star className="size-4" />}
          label="Por acerto"
          value={`${quiz.point_reward_per_correct} pontos`}
        />
        {quiz.coin_reward_per_correct > 0 ? (
          <StatTile
            icon={<Coins className="size-4" />}
            label="Por acerto"
            value={`${quiz.coin_reward_per_correct} moedas`}
          />
        ) : null}
        <StatTile
          icon={<Clock className="size-4" />}
          label="Tempo"
          value={formatTimeLimit(quiz.time_limit_in_seconds)}
        />
        <StatTile
          icon={<ListChecks className="size-4" />}
          label="Tentativas"
          value={
            quiz.attempts_left === null || quiz.attempts_left === undefined
              ? formatAttempts(quiz.max_attempts)
              : `${quiz.attempts_left} restante(s)`
          }
        />
        {quiz.passing_score_percentage ? (
          <StatTile
            icon={<Trophy className="size-4" />}
            label="Nota de corte"
            value={`${quiz.passing_score_percentage}% de acerto`}
          />
        ) : null}
      </div>

      {/* A pontuação que está valendo hoje — é o que o aluno tem a perder ao refazer. */}
      {score ? (
        <Box variant="purple" className="p-4">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-xs font-semibold uppercase tracking-wide text-[#7F7FAD]">
                Sua pontuação atual
              </p>
              <p className="font-hl text-2xl font-black text-american-blue">
                {score.correct_count}/{score.total_count}
                <span className="ml-2 text-base font-bold text-[#7F7FAD]">
                  {score.score_percentage ?? 0}%
                </span>
              </p>
            </div>
            <div className="shrink-0 text-right">
              <p className="font-hl text-lg font-black text-jacaranda">
                {score.points_awarded} pts
              </p>
              {score.coins_awarded > 0 ? (
                <p className="text-sm font-bold text-[#7F7FAD]">
                  {score.coins_awarded} moedas
                </p>
              ) : null}
            </div>
          </div>
          {score.passed !== null ? (
            <p
              className={
                score.passed
                  ? 'mt-2 text-sm font-bold text-[#2E7D53]'
                  : 'mt-2 text-sm font-bold text-[#B23B41]'
              }
            >
              {score.passed ? 'Aprovado' : 'Abaixo da nota de corte'}
            </p>
          ) : null}
        </Box>
      ) : null}

      {/* O quiz pontua uma vez: refazer substitui, não soma. */}
      {canRepeat ? (
        <div className="flex items-start gap-3 rounded-xl border-2 border-[#E8B04B]/50 bg-[#F5ECD4] p-4">
          <RotateCcw
            className="mt-0.5 size-5 shrink-0 text-[#9A6B12]"
            aria-hidden
          />
          <p className="text-sm font-semibold leading-snug text-[#7A5410]">
            {score
              ? 'Este quiz vale uma pontuação só. Se você refizer, a pontuação acima é apagada e passa a valer a da nova tentativa — mesmo que seja menor.'
              : 'Este quiz vale uma pontuação só. Se você refizer depois, a pontuação da nova tentativa substitui a anterior — mesmo que seja menor.'}
          </p>
        </div>
      ) : null}

      {errorMessage ? (
        <p className="text-center text-sm font-semibold text-[#B23B41]">
          {errorMessage}
        </p>
      ) : null}

      {exhausted ? (
        <p className="text-center text-sm font-semibold text-[#7F7FAD]">
          Você já usou todas as tentativas deste quiz.
        </p>
      ) : (
        <Button
          className="mx-auto"
          loading={isStarting}
          onClick={onStart}
        >
          {score ? 'Refazer quiz' : 'Começar'}
        </Button>
      )}
    </div>
  )
}
